<script setup lang="ts">
import { ref, watch, computed, nextTick } from 'vue'
import { useDebounceFn } from '@vueuse/core';

import { useFetch } from '#imports'
import {
  MglMap,
  MglNavigationControl,
  MglMarker,
  MglPopup,
  useMap
} from '@indoorequal/vue-maplibre-gl'
import type { LngLatLike, Map } from 'maplibre-gl'

const props = defineProps<{
  modelValue: {
    coordinates: [number, number] // [latitude, longitude]
    formatted_address?: string
    address_components?: Record<string, any> | null
  }
  popupContent?: string
  fullscreen?: boolean
}>()

const mapLoaded = ref(false)
const map = useMap()
const isFlying = ref(false)
const dragging = ref(false)
const localMarkerPosition = ref<LngLatLike | null>(null)
const isGeocoding = ref(false)

// Store a reference to the marker
const markerRef = ref<any>(null)

// Add a function to handle initial positioning
const handleInitialPosition = () => {
  if (props.modelValue?.coordinates?.length === 2) {
    const [lat, lng] = props.modelValue.coordinates
    flyToLocation(lng, lat)
  }
}

// Update the map load handler
const onMapLoad = () => {
  mapLoaded.value = true
  // Delay initial positioning to ensure map is fully ready
  nextTick(() => {
    setTimeout(() => {
      handleInitialPosition()
    }, 100)
  })
}

const flyToLocation = (lng: number, lat: number) => {
  // Don't fly during drag operations or if already flying
  if (dragging.value || isFlying.value || !mapLoaded.value || !map.map) return
  
  isFlying.value = true
  map.map.flyTo({
    center: [lng, lat],
    zoom: 15,
    duration: 1500, // Reduced duration for less jarring movement
    essential: true,
    curve: 1.2, // Gentler curve
    speed: 0.8, // Slower speed
  })
  
  // Reset flying state after animation completes
  setTimeout(() => {
    isFlying.value = false
  }, 1600) // Slightly longer than duration to ensure completion
}

// Update the watch to prevent flying during drag and add debouncing
watch(() => props.modelValue, (newValue) => {
  if (newValue?.coordinates?.length === 2 && !dragging.value && !isFlying.value) {
    const [lat, lng] = newValue.coordinates
    // Add a small delay to prevent rapid successive calls
    setTimeout(() => {
      if (!dragging.value) { // Double-check dragging state
        flyToLocation(lng, lat)
      }
    }, 50)
  }
}, { immediate: true, deep: true })

// Watch for map load separately with better timing
watch(() => map.isLoaded, (isLoaded) => {
  if (isLoaded) {
    mapLoaded.value = true
    // Delay initial positioning to ensure everything is ready
    nextTick(() => {
      setTimeout(() => {
        handleInitialPosition()
      }, 200)
    })
  }
})

const emit = defineEmits<{
  'update:modelValue': [{
    coordinates: [number, number]
    formatted_address?: string
    address_components?: Record<string, any> | null
  }]
  'showAddressUpdate': [string, Record<string, any>?]
}>()

// Update center to use existing coordinates if available
const center = computed<LngLatLike>(() => {
  if (props.modelValue?.coordinates?.length === 2) {
    const [lat, lng] = props.modelValue.coordinates
    return [lng, lat] // MapLibre expects [longitude, latitude]
  }
  return [144.9631, -37.8136] // Default to Melbourne
})

const zoom = ref(12)

// Update markerPosition to use local position during dragging
const markerPosition = computed<LngLatLike>(() => {
  // Use local position during dragging or geocoding to prevent jumping
  if ((dragging.value || isGeocoding.value) && localMarkerPosition.value) {
    return localMarkerPosition.value
  }
  
  if (props.modelValue?.coordinates?.length === 2) {
    const [lat, lng] = props.modelValue.coordinates
    return [lng, lat] // MapLibre expects [longitude, latitude]
  }
  return [144.946457, -37.840935] // Default to Melbourne
})

// Update click handler to use debounced emit
const handleMapClick = ({ event }: { event: { lngLat: { lng: number, lat: number } } }) => {
  // Don't handle clicks during drag or fly operations
  if (dragging.value || isFlying.value) return
  
  const { lng, lat } = event.lngLat
  
  // Emit coordinates immediately for click events
  emit('update:modelValue', {
    coordinates: [lat, lng],
    formatted_address: props.modelValue?.formatted_address,
    address_components: props.modelValue?.address_components
  })
  
  // Trigger reverse geocoding for address update after a small delay
  setTimeout(() => {
    if (!dragging.value && !isFlying.value) {
      debouncedReverseGeocode(lat, lng)
    }
  }, 150)
}

// Create a type for marker coordinates
type MarkerCoordinates = {
  lat: number;
  lng: number;
} | [number, number]

// Helper function to extract lat/lng from coordinates
const getLatLng = (coordinates: MarkerCoordinates): { lat: number; lng: number } => {
  if (Array.isArray(coordinates)) {
    return { lng: coordinates[0], lat: coordinates[1] }
  }
  return coordinates
}

// Create a debounced version of the reverse geocoding function
const debouncedReverseGeocode = useDebounceFn(async (lat: number, lng: number) => {
  isGeocoding.value = true
  
  try {
    // Get the address for these coordinates
    const { data: locationData } = await useFetch('/api/geocode', {
      method: 'POST',
      body: { 
        coordinates: [lat, lng],
        useGoogle: true
      }
    })

    if (locationData.value?.status === 'OK' && locationData.value?.formatted_address) {
      // Emit both coordinates and address - this ensures coordinates are correct
      emit('update:modelValue', {
        coordinates: [lat, lng], // Ensure consistent [lat, lng] format
        formatted_address: locationData.value.formatted_address,
        address_components: locationData.value.address_components
      })
      
      // Also emit the address update prompt
      emit('showAddressUpdate', locationData.value.formatted_address, locationData.value.address_components)
    }
  } catch (error) {
    console.error('Error reverse geocoding:', error)
  } finally {
    // Clear flags after geocoding completes (success or error)
    isGeocoding.value = false
    localMarkerPosition.value = null
  }
}, 500)

// Update marker coordinates handler - only during dragging
const handleMarkerCoordinatesChange = (coordinates: LngLatLike) => {
  if (!dragging.value) return // Only handle during dragging
  
  const coords = getLatLng(coordinates as MarkerCoordinates)
  
  // Store local position during drag - MapLibre coordinates are [lng, lat]
  localMarkerPosition.value = [coords.lng, coords.lat]
}

// Handle drag start
const handleDragStart = () => {
  dragging.value = true
  isFlying.value = false // Stop any ongoing fly animation
  
  // Initialize local position with current marker position
  if (props.modelValue?.coordinates?.length === 2) {
    const [lat, lng] = props.modelValue.coordinates
    // Store in MapLibre format [lng, lat]
    localMarkerPosition.value = [lng, lat]
  }
}

// Update dragend handler to trigger reverse geocoding
const handleDragEnd = () => {
  // Get final position before clearing drag state
  const finalPosition = localMarkerPosition.value
  
  // Clear drag state but keep local position until geocoding completes
  dragging.value = false
  
  if (finalPosition) {
    // finalPosition is in MapLibre format [lng, lat]
    const [lng, lat] = finalPosition as [number, number]
    
    // Keep local position until reverse geocoding completes
    // This prevents jumping back to initial position
    
    // Let reverse geocoding handle the coordinate update and address
    debouncedReverseGeocode(lat, lng)
  }
}

// Add state to track original values
const originalCoordinates = ref<[number, number] | null>(null)
const originalAddress = ref<string | null>(null)

// Update the watch to store original values when they first change
watch(() => props.modelValue, (newValue) => {
  if (newValue?.coordinates?.length === 2 && !originalCoordinates.value) {
    originalCoordinates.value = [...newValue.coordinates]
    originalAddress.value = newValue.formatted_address || null
  }
}, { immediate: true, deep: true })

// Add method to revert changes
const revertChanges = () => {
  if (originalCoordinates.value) {
    emit('update:modelValue', {
      coordinates: originalCoordinates.value,
      formatted_address: originalAddress.value || undefined,
      address_components: props.modelValue?.address_components
    })
    
    // Reset the map position
    const [lat, lng] = originalCoordinates.value
    flyToLocation(lng, lat)
  }
}

// Add method to commit changes
const commitChanges = () => {
  // Update original values to current values
  if (props.modelValue?.coordinates) {
    originalCoordinates.value = [...props.modelValue.coordinates]
    originalAddress.value = props.modelValue.formatted_address || null
  }
}

// Expose methods to parent
defineExpose({
  revertChanges,
  commitChanges
})

const colorMode = useColorMode()

const mapStyle = computed(() => {
  return colorMode.value === 'dark'
    ? 'https://api.maptiler.com/maps/5d061ce6-6981-4fda-8752-ffc8cca65c0d/style.json?key=tVYcCqbqU5I3tpqgTmCu'
    : 'https://api.maptiler.com/maps/landscape/style.json?key=tVYcCqbqU5I3tpqgTmCu'
})

</script>

<template>
  <div :class="[
    'rounded-md overflow-hidden',
    colorMode.value === 'dark' ? 'hue-rotate-0' : '',
    fullscreen ? 'absolute inset-0 -z-10' : 'w-full h-[200px]'
  ]">
    <ClientOnly>
      <MglMap
        :mapStyle="mapStyle"
        :attributionControl="false"
        :center="center"
        :zoom="zoom"
        @map:load="onMapLoad"
        @map:click="handleMapClick"
      >
        
        <MglMarker
          ref="markerRef"
          :coordinates="markerPosition"
          color="red"
          draggable
          @dragend="handleDragEnd"
          @dragstart="handleDragStart"
          @update:coordinates="handleMarkerCoordinatesChange"
        >
          <MglPopup v-if="props.modelValue?.formatted_address" :offset="[0, 25]">
            <div class="p-2 bg-background/80 backdrop-blur-sm">
              {{ props.modelValue.formatted_address }}
            </div>
          </MglPopup>
        </MglMarker>
      </MglMap>
    </ClientOnly>
  </div>
</template>

<style>
@import "maplibre-gl/dist/maplibre-gl.css";
</style> 